<div class="queue-container">
  <div class="queue-header">
    <h2>Provider Timesheets</h2>
    <span class="spacer"></span>
    <button mat-icon-button (click)="load()" matTooltip="Refresh" [disabled]="loading()">
      <mat-icon>refresh</mat-icon>
    </button>
  </div>

  @if (loading()) {
    <div class="queue-loading"><mat-spinner diameter="36"></mat-spinner></div>
  } @else {
    @if (adminView()) {
      <div class="admin-note">Viewing all timesheets awaiting action · System Administrator</div>
    }
    @if (truncated()) {
      <div class="admin-note truncated-note">
        Showing the oldest submissions only — the server capped this list, so newer items are missing.
      </div>
    }
    <mat-tab-group>
      <mat-tab label="{{ adminView() ? 'For Review' : 'For My Review' }} ({{ reviewQueue().length }})">
        <ng-container *ngTemplateOutlet="list; context: { rows: reviewQueue(), tab: 'review' }"></ng-container>
      </mat-tab>
      <mat-tab label="{{ adminView() ? 'For Approval' : 'For My Approval' }} ({{ approvalQueue().length }})">
        <ng-container *ngTemplateOutlet="list; context: { rows: approvalQueue(), tab: 'approval' }"></ng-container>
      </mat-tab>
      <!-- Payment-stage orgs get this tab as a work list (rows assigned to the
           accountant). Orgs that pay outside the app (cbhi → NetSuite) get it as a
           STATUS list instead — approved-and-awaiting-payment, nobody assigned —
           populated for System Administrators by loadApprovedPipeline(). Read-only
           there: no in-app payment action (see the actions block). -->
      @if (showPaymentStage || paymentQueue().length) {
        <mat-tab label="{{ showPaymentStage ? 'Approved For Payment' : 'Awaiting Payment' }} ({{ paymentQueue().length }})">
          <ng-container *ngTemplateOutlet="list; context: { rows: paymentQueue(), tab: 'payment' }"></ng-container>
        </mat-tab>
      }
    </mat-tab-group>
  }
</div>

<ng-template #list let-rows="rows" let-tab="tab">
  <div class="queue-list">
    @if (tab === 'payment' && !showPaymentStage && rows.length) {
      <p class="tab-note">
        Approved and waiting on payment from NetSuite. Nobody has to act on these —
        they clear once the payment is recorded.
      </p>
    }
    @if (rows.length === 0) {
      <p class="queue-empty">Nothing here right now.</p>
    }
    @for (row of rows; track row.submissionId) {
      <mat-card class="queue-card">
        <div class="queue-card-main">
          <div class="queue-card-info">
            <span class="hcp-name">{{ partyName(row) }}</span>
            @if (row.status) {
              <span class="status-chip">{{ statusLabel(row.status) }}</span>
            }
          </div>
          <div class="queue-card-meta">
            <span class="period">{{ period(row) }}</span>
            @if (row.frequency) { <span class="freq">{{ row.frequency }}</span> }
            <span>{{ formatHours(row.totalHours) }} hrs</span>
            @if (row.totalAmount != null) { <span>{{ formatAmount(row.totalAmount) }}</span> }
          </div>
        </div>

        @if (row.party || row.timesheetType) {
          <div class="queue-card-spec">
            @if (row.party) {
              <span class="spec-field"><span class="spec-label">Party</span><span class="spec-value">{{ row.party }}</span></span>
            }
            @if (row.timesheetType) {
              <span class="spec-field"><span class="spec-label">Timesheet Type</span><span class="spec-value">{{ row.timesheetType }}</span></span>
            }
          </div>
        }

        @if (breakdownFor(row).length) {
          <div class="queue-card-activities">
            @if (amountsReliableFor(row)) {
              <span class="activities-label">Hours &amp; amount by activity</span>
              <div class="activity-lines">
                @for (line of breakdownFor(row); track line.name) {
                  <span class="activity-line">
                    <span class="activity-name">{{ line.name }}</span>
                    <span class="activity-hours">{{ formatHours(line.hours) }} hrs</span>
                    <span class="activity-amount">{{ formatAmount(line.amount) }}</span>
                  </span>
                }
                <span class="activity-line activity-total">
                  <span class="activity-name">Total</span>
                  <span class="activity-hours">{{ formatHours(row.totalHours) }} hrs</span>
                  <span class="activity-amount">{{ formatAmount(row.totalAmount) }}</span>
                </span>
              </div>
            } @else {
              <span class="activities-label">Hours by activity</span>
              <div class="activity-lines">
                @for (line of breakdownFor(row); track line.name) {
                  <span class="activity-line">
                    <span class="activity-name">{{ line.name }}</span>
                    <span class="activity-hours">{{ formatHours(line.hours) }} hrs</span>
                  </span>
                }
                <span class="activity-line activity-total">
                  <span class="activity-name">Total</span>
                  <span class="activity-hours">{{ formatHours(row.totalHours) }} hrs</span>
                </span>
              </div>
              <p class="amounts-unavailable">
                Per-activity amounts aren’t shown — pay rates couldn’t be loaded for this period.
                Hours are accurate; the period total is {{ formatAmount(row.totalAmount) }}.
              </p>
            }
          </div>
        }

        <div class="queue-card-actions">
          @if (tab === 'payment' && !showPaymentStage) {
            <!-- Payment happens in NetSuite for this org, so no in-app pay action. -->
            <span class="payment-external-note">Approved · paid outside the app (NetSuite)</span>
          } @else if (tab === 'payment') {
            <mat-form-field appearance="outline" class="check-number-field" subscriptSizing="dynamic">
              <mat-label>Check #</mat-label>
              <input matInput
                [ngModel]="checkNumberFor(row)"
                (ngModelChange)="setCheckNumber(row, $event)"
                [disabled]="acting()"
                placeholder="Enter check number">
            </mat-form-field>
            <button mat-flat-button color="primary"
              [disabled]="acting() || !hasCheckNumber(row)"
              matTooltip="Enter a check number to enable"
              [matTooltipDisabled]="hasCheckNumber(row)"
              (click)="payAndMarkPaid(row)">
              Approve for Payment &amp; Mark Paid
            </button>
          } @else {
            <button mat-flat-button color="primary" [disabled]="acting()"
              (click)="approve(row, tab)">Approve</button>
          }
          @if (canReject(row, tab)) {
            <button mat-stroked-button [disabled]="acting()"
              (click)="startNegative(row)">{{ negativeLabel(tab) }}</button>
          }
          <span class="actions-spacer"></span>
          <button mat-button [disabled]="acting()" (click)="openDetail(row)">
            <mat-icon>open_in_new</mat-icon> Details
          </button>
        </div>

        @if (isOpen(row)) {
          <div class="queue-reason">
            <mat-form-field appearance="outline" class="reason-field" subscriptSizing="dynamic">
              <mat-label>Note (required)</mat-label>
              <textarea matInput rows="2" [(ngModel)]="noteText"
                placeholder="Why is this being sent back? The owner is emailed this."></textarea>
            </mat-form-field>
            <div class="queue-reason-actions">
              <button mat-button (click)="cancelNegative()">Cancel</button>
              <button mat-flat-button color="warn" [disabled]="acting()"
                (click)="confirmNegative(row, tab)">Confirm</button>
            </div>
          </div>
        }
      </mat-card>
    }
  </div>
</ng-template>
